import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useClientTeams } from '@/hooks/useClientTeams';
import { useSupabaseUser } from '@/hooks/use-supabase-user';

export const EmergencyWidget = () => {
  const { isAdmin } = useUserRoles();
  const { teams: clientTeams } = useClientTeams();
  const { user } = useSupabaseUser();

  const { data: emergencyData, isLoading } = useQuery({
    queryKey: ['emergency-widget', user?.id, isAdmin, clientTeams],
    queryFn: async () => {
      if (!user?.id) return { total: 0, pending: 0, resolved: 0, critical: 0, recent: 0, reports: [] };

      let query = supabase
        .from('emergency_reports')
        .select('id, status, severity, created_at, title, team_id')
        .order('created_at', { ascending: false });

      // If not admin, filter by team access
      if (!isAdmin) {
        // Get user's teams (as team member or client)
        const userTeamIds = [...clientTeams];
        
        // Also get teams where user is a team member
        const { data: teamMembers } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id);
          
        if (teamMembers) {
          userTeamIds.push(...teamMembers.map(tm => tm.team_id));
        }
        
        // Get teams where user is a manager
        const { data: managerTeams } = await supabase
          .from('manager_teams')
          .select('team_id')
          .eq('manager_id', user.id);
          
        if (managerTeams) {
          userTeamIds.push(...managerTeams.map(mt => mt.team_id));
        }
        
        // Remove duplicates
        const uniqueTeamIds = [...new Set(userTeamIds)];
        
        if (uniqueTeamIds.length > 0) {
          query = query.in('team_id', uniqueTeamIds);
        } else {
          // If user has no team access, return empty data
          return { total: 0, pending: 0, resolved: 0, critical: 0, recent: 0, reports: [] };
        }
      }

      query = query.limit(10);
      const { data: reports } = await query;

      const pending = reports?.filter(r => r.status === 'pending').length || 0;
      const resolved = reports?.filter(r => r.status === 'resolved').length || 0;
      const critical = reports?.filter(r => r.severity === 'critical').length || 0;

      // Get reports from last 24 hours
      const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const recent = reports?.filter(r => new Date(r.created_at) > last24h).length || 0;

      return {
        total: reports?.length || 0,
        pending,
        resolved,
        critical,
        recent,
        reports: reports || []
      };
    },
    refetchInterval: 10000, // More frequent for emergencies
    enabled: !!user?.id
  });

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-6 h-6 text-red-500" />
        <h3 className="text-lg font-semibold">Emergency Reports</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{emergencyData?.pending || 0}</div>
          <div className="text-sm text-muted-foreground">Pending</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">{emergencyData?.critical || 0}</div>
          <div className="text-sm text-muted-foreground">Critical</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Last 24h</span>
          </div>
          <Badge variant="outline">{emergencyData?.recent || 0}</Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm">Resolved</span>
          </div>
          <Badge variant="default">{emergencyData?.resolved || 0}</Badge>
        </div>
      </div>

      {emergencyData?.reports && emergencyData.reports.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-sm font-medium mb-2">Recent Reports</h4>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {emergencyData.reports.slice(0, 3).map((report) => (
              <div key={report.id} className="text-xs p-2 bg-muted rounded flex justify-between">
                <span className="truncate">{report.title}</span>
                <Badge variant={report.status === 'pending' ? 'destructive' : 'default'}>
                  {report.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};